import { useSettingStore } from 'shared/setting/stores/settingStore';
import type { LogYardDataResponse as LogYardDataResponse } from '@/storageAreas/api/models/storageAreaResponse';
import type { LogYardProductContentModel } from '@/storageAreas/logYards/components/LogYardContent.vue';
import { defineStore, storeToRefs } from 'pinia';
import type { Product } from 'shared/products/domain/product';
import { useProductStore } from 'shared/products/stores/productStore';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { computed, ref } from 'vue';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';

export interface EditLogYard {
  storageAreaId: string;
  logYard: LogYardDataResponse;
  selectedStorageAreaIds: string[];
}

export interface CopyLogYard {
  logYard: LogYardDataResponse;
}

export const useSelectedLogYardStore = defineStore('selectedLogYardStore', () => {
  const operation = ref<'create' | 'edit' | 'none'>('none');

  const productStore = useProductStore();
  const storageAreaStore = useStorageAreaStore();
  const languageStore = useLanguageStore();
  const settingStore = useSettingStore();
  const { translations } = storeToRefs(languageStore);
  const { setting } = storeToRefs(settingStore);

  let storageAreaId: string | null = null;
  const selectedStorageAreaIds = ref<string[]>([]);
  const editMultiple = computed(() => selectedStorageAreaIds.value.length > 1);

  const model = ref<LogYardDataResponse>();

  const automaticProductChange = ref<boolean>(true);
  const automaticProductChangeInitial = computed(() => {
    if (!model.value) return;
    return !model.value.disableAutomaticProductChange;
  });
  const automaticProductChangeChanged = computed(
    () =>
      operation.value === 'edit' &&
      automaticProductChange.value !== automaticProductChangeInitial.value,
  );
  const automaticProductChangeEnabled = computed<boolean>(() => {
    if (!editMultiple.value) return true;

    const storageAreas = selectedStorageAreaIds.value
      .map((id) => storageAreaStore.getById(id))
      .filter((s) => s?.logYardData !== null && s?.logYardData !== undefined);
    if (storageAreas.length === 0) return false;

    const firstValue = !storageAreas[0]!.logYardData!.disableAutomaticProductChange;
    return storageAreas.every(
      (sa) => !sa!.logYardData!.disableAutomaticProductChange === firstValue,
    );
  });

  const allowMultipleProducts = ref<boolean>(false);
  const allowMultipleProductsInitial = computed(() => {
    if (!model.value) return;
    return model.value.allowMultipleProducts;
  });
  const allowMultipleProductsChanged = computed(
    () =>
      operation.value === 'edit' &&
      allowMultipleProducts.value !== allowMultipleProductsInitial.value,
  );
  const allowMultipleProductsEnabled = computed<boolean>(() => {
    if (!editMultiple.value) return true;

    const storageAreas = selectedStorageAreaIds.value
      .map((id) => storageAreaStore.getById(id))
      .filter((s) => s?.logYardData !== null && s?.logYardData !== undefined);
    if (storageAreas.length === 0) return false;

    const firstValue = storageAreas[0]!.logYardData!.allowMultipleProducts;
    return storageAreas.every((sa) => sa!.logYardData!.allowMultipleProducts === firstValue);
  });

  const allowedProducts = ref<Product[]>([]);
  const allowedProductsInitial = computed(() => {
    if (!model.value) return [];
    return model.value.allowedProducts;
  });
  const allowedProductsChanged = computed(() => {
    if (operation.value !== 'edit') return false;
    const newValue = new Set(allowedProducts.value.map((p) => p.id));
    const currentValue = new Set(allowedProductsInitial.value);

    if (currentValue.size !== newValue.size) return true;

    for (const v of currentValue) {
      if (!newValue.has(v)) {
        return true;
      }
    }

    for (const v of newValue) {
      if (!currentValue.has(v)) {
        return true;
      }
    }

    return false;
  });
  const allowedProductsEnabled = computed<boolean>(() => {
    if (!editMultiple.value) return true;

    const storageAreas = selectedStorageAreaIds.value
      .map((id) => storageAreaStore.getById(id))
      .filter((s) => s?.logYardData !== null && s?.logYardData !== undefined);
    if (storageAreas.length === 0) return false;

    const firstProducts = new Set(storageAreas[0]!.logYardData!.allowedProducts);
    return storageAreas.every((sa) => {
      const products = new Set(sa!.logYardData!.allowedProducts);
      if (products.size !== firstProducts.size) return false;
      for (const p of products) {
        if (!firstProducts.has(p)) return false;
      }
      return true;
    });
  });

  const sideArea = ref<number>(0);
  const sideAreaText = ref<string | number | undefined>('0');
  const sideAreaInitial = computed(() => {
    if (!model.value) return [];
    return model.value.sideArea;
  });
  const sideAreaChanged = computed(() => {
    if (operation.value !== 'edit') return false;
    return sideAreaText.value !== sideAreaInitial.value;
  });
  const sideAreaError = computed<string | undefined>(() => {
    if (typeof sideAreaText.value !== 'number') {
      return translations.value.error.required;
    }

    return undefined;
  });
  const sideAreaEnabled = computed<boolean>(() => {
    if (!editMultiple.value) return true;

    const storageAreas = selectedStorageAreaIds.value
      .map((id) => storageAreaStore.getById(id))
      .filter((s) => s?.logYardData !== null && s?.logYardData !== undefined);
    if (storageAreas.length === 0) return false;

    const firstValue = storageAreas[0]!.logYardData!.sideArea;
    return storageAreas.every((sa) => sa!.logYardData!.sideArea === firstValue);
  });

  const contents = computed(() => {
    const result: LogYardProductContentModel[] = [];

    if (model.value) {
      for (const content of model.value.content) {
        const product = productStore.getById(content.productId);
        if (!product) continue;
        result.push({
          numberOfLogs: content.numberOfLogs,
          productId: product.id,
          productName: product.name,
          volume: content.volume,
        });
      }
    }

    return result;
  });

  const logYardChanged = computed(() => {
    return (
      automaticProductChangeChanged.value ||
      allowMultipleProductsChanged.value ||
      allowedProductsChanged.value ||
      sideAreaChanged.value
    );
  });

  const invalid = computed(() => sideAreaError.value !== undefined);

  storageAreaStore.changed.on((ev) => {
    if (ev.id !== storageAreaId) return;
    if (ev.action === 'removed') return;
    const logYard = ev.data.logYardData;
    if (logYard) {
      fill(logYard, false);
    }
    model.value = logYard ?? undefined;
  });

  function create() {
    storageAreaId = null;
    operation.value = 'create';
    model.value = undefined;
    selectedStorageAreaIds.value = [];

    automaticProductChange.value = true;
    allowMultipleProducts.value = false;
    allowedProducts.value = [];

    contents.value.length = 0;

    sideArea.value = setting.value.sharedClient.storageArea.logYardSideArea;
    sideAreaText.value = setting.value.sharedClient.storageArea.logYardSideArea;
  }

  function edit(action: EditLogYard) {
    selectedStorageAreaIds.value = action.selectedStorageAreaIds;
    const isFirstSelection = selectedStorageAreaIds.value.length === 1;

    if (isFirstSelection) {
      // First/single selection - initialize normally
      storageAreaId = action.storageAreaId;
      const logYard = action.logYard;
      fill(logYard, true);
      model.value = logYard;
    } else if (editMultiple.value) {
      // Multiple selections - update only matching fields
      const storageAreas = selectedStorageAreaIds.value
        .map((id) => storageAreaStore.getById(id))
        .filter((s) => s?.logYardData !== null && s?.logYardData !== undefined);

      if (automaticProductChangeEnabled.value) {
        const firstValue = !storageAreas[0]!.logYardData!.disableAutomaticProductChange;
        if (
          storageAreas.every((sa) => !sa!.logYardData!.disableAutomaticProductChange === firstValue)
        ) {
          automaticProductChange.value = firstValue;
        }
      }

      if (allowMultipleProductsEnabled.value) {
        const firstValue = storageAreas[0]!.logYardData!.allowMultipleProducts;
        if (storageAreas.every((sa) => sa!.logYardData!.allowMultipleProducts === firstValue)) {
          allowMultipleProducts.value = firstValue;
        }
      }

      if (allowedProductsEnabled.value) {
        const firstProducts = new Set(storageAreas[0]!.logYardData!.allowedProducts);
        const allMatch = storageAreas.every((sa) => {
          const products = new Set(sa!.logYardData!.allowedProducts);
          if (products.size !== firstProducts.size) return false;
          for (const p of products) {
            if (!firstProducts.has(p)) return false;
          }
          return true;
        });
        if (allMatch) {
          allowedProducts.value = [];
          for (const productId of storageAreas[0]!.logYardData!.allowedProducts) {
            const product = productStore.getOrThrow(productId);
            allowedProducts.value.push(product);
          }
          allowedProducts.value.sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { numeric: true }),
          );
        }
      }

      if (sideAreaEnabled.value) {
        const firstValue = storageAreas[0]!.logYardData!.sideArea;
        if (storageAreas.every((sa) => sa!.logYardData!.sideArea === firstValue)) {
          sideArea.value = firstValue;
          sideAreaText.value = firstValue;
        }
      }
    }

    operation.value = 'edit';
  }

  function copy(action: CopyLogYard) {
    storageAreaId = null;
    operation.value = 'create';
    selectedStorageAreaIds.value = [];

    const logYard = action.logYard;
    fill(logYard, true);
    model.value = undefined;

    contents.value.length = 0;
  }

  function fill(logYard: LogYardDataResponse, overwriteChanges: boolean) {
    if (overwriteChanges || !automaticProductChangeChanged.value) {
      automaticProductChange.value = !logYard.disableAutomaticProductChange;
    }

    if (overwriteChanges || !allowMultipleProductsChanged.value) {
      allowMultipleProducts.value = logYard.allowMultipleProducts;
    }

    if (overwriteChanges || !allowedProductsChanged.value) {
      allowedProducts.value = [];

      for (const productId of logYard.allowedProducts) {
        const product = productStore.getOrThrow(productId);
        allowedProducts.value.push(product);
      }

      allowedProducts.value.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true }),
      );
    }

    if (overwriteChanges || !sideAreaChanged.value) {
      sideArea.value = logYard.sideArea;
      sideAreaText.value = logYard.sideArea;
    }
  }

  function hide() {
    storageAreaId = null;
    operation.value = 'none';
    selectedStorageAreaIds.value = [];
    automaticProductChange.value = true;
    allowMultipleProducts.value = false;
    allowedProducts.value = [];
  }

  return {
    create,
    edit,
    copy,
    hide,
    operation,
    selectedStorageAreaIds,
    editMultiple,
    automaticProductChange,
    automaticProductChangeChanged,
    automaticProductChangeEnabled,
    allowMultipleProducts,
    allowMultipleProductsChanged,
    allowMultipleProductsEnabled,
    allowedProducts,
    allowedProductsChanged,
    allowedProductsEnabled,
    logYardChanged,
    contents,
    sideArea,
    sideAreaText,
    sideAreaChanged,
    sideAreaError,
    sideAreaEnabled,
    invalid,
  };
});
